import sgMail from '@sendgrid/mail';
import { Resend } from 'resend';
import { BaseIntegration } from '../../core/BaseIntegration';
import type { IntegrationConfig, IntegrationResult, IntegrationError, IntegrationParams } from '../../types';
import { registerIntegration } from '../../registry';

type EmailProvider = 'sendgrid' | 'resend';

/**
 * Email integration (SendGrid/Resend)
 */
export class EmailIntegration extends BaseIntegration {
  private provider: EmailProvider;
  private fromEmail: string;
  private resendClient?: Resend;

  constructor(config: IntegrationConfig) {
    super(config);

    this.provider = (config.env.PROVIDER as EmailProvider) || 'sendgrid';
    this.fromEmail = config.env.FROM_EMAIL || 'noreply@example.com';

    if (this.provider === 'sendgrid') {
      const apiKey = config.env.SENDGRID_API_KEY;
      if (!apiKey) {
        throw new Error('SENDGRID_API_KEY not configured');
      }
      sgMail.setApiKey(apiKey);
    } else if (this.provider === 'resend') {
      const apiKey = config.env.RESEND_API_KEY;
      if (!apiKey) {
        throw new Error('RESEND_API_KEY not configured');
      }
      this.resendClient = new Resend(apiKey);
    }

    this.logger.info(`Email integration initialized (${this.provider})`);
  }

  async execute(
    action: string,
    params: IntegrationParams,
  ): Promise<IntegrationResult> {
    // Validate params against registry
    const validation = this.validateParams(action, params);
    if (!validation.valid) {
      return {
        success: false,
        error: {
          name: 'IntegrationError',
          message: 'Validation failed',
          code: 'VALIDATION_ERROR',
          details: validation.errors,
        } as IntegrationError,
        metadata: this.createMetadata(action, 0),
      };
    }

    const startTime = Date.now();
    let retries = 0;

    try {
      let data: unknown;

      switch (action) {
        case 'send':
          data = await this.executeWithRetry(() => this.send(params));
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      return {
        success: true,
        data,
        metadata: this.createMetadata(action, Date.now() - startTime, retries),
      };
    } catch (error) {
      return this.handleError(action, error);
    }
  }

  private async send(params: IntegrationParams) {
    const { to, subject, body, from } = params;

    this.logger.debug('Sending email', { to: String(to), subject: String(subject), provider: this.provider });

    if (this.provider === 'sendgrid') {
      return await this.sendViaSendGrid(
        to as string,
        subject as string,
        body as string,
        (from as string) || this.fromEmail,
      );
    } else if (this.provider === 'resend') {
      return await this.sendViaResend(
        to as string,
        subject as string,
        body as string,
        (from as string) || this.fromEmail,
      );
    }

    throw new Error(`Unknown email provider: ${this.provider}`);
  }

  private async sendViaSendGrid(
    to: string,
    subject: string,
    body: string,
    from: string,
  ) {
    const msg = {
      to,
      from,
      subject,
      html: body,
    };

    const response = await sgMail.send(msg);

    return {
      id: response[0].headers['x-message-id'],
      status: 'sent',
    };
  }

  private async sendViaResend(
    to: string,
    subject: string,
    body: string,
    from: string,
  ) {
    if (!this.resendClient) {
      throw new Error('Resend client not initialized');
    }

    const response = await this.resendClient.emails.send({
      from,
      to,
      subject,
      html: body,
    });

    return {
      id: response.data?.id,
      status: 'sent',
    };
  }
}

// Auto-register
registerIntegration('email', EmailIntegration);
